import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Switch } from './ui/switch'
import { cn } from '@/lib/utils'
import { Building2, Edit, Loader2, User } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useUpdateVendorMutation,
  type Vendor,
  type VendorType,
} from '@/redux/service/vendor'
import SkillsInput from './SkillsInput'

const EditVendorSchema = Yup.object({
  vendor_name: Yup.string().required('Vendor name is required'),
  vendor_type: Yup.string()
    .oneOf(['freelance', 'company'])
    .required('Vendor type is required'),
  contact_name: Yup.string().required('Contact name is required'),
  contact_email: Yup.string()
    .email('Invalid email address')
    .required('Contact email is required'),
  contact_phone: Yup.string().required('Contact phone is required'),
  default_rate: Yup.number().typeError('Must be a number').min(0),
  freelancer_name: Yup.string().when('vendor_type', {
    is: 'freelance',
    then: schema => schema.required('Freelancer name is required'),
    otherwise: schema => schema,
  }),
  company_name: Yup.string().when('vendor_type', {
    is: 'company',
    then: schema => schema.required('Company name is required'),
    otherwise: schema => schema,
  }),
  address: Yup.string(),
})

interface Props {
  open: boolean
  onClose: () => void
  vendorToEdit: Vendor
}

interface FormValues {
  vendor_name: string
  vendor_type: VendorType
  contact_name: string
  contact_email: string
  contact_phone: string
  default_rate: number | string
  freelancer_name: string
  company_name: string
  address: string
  tech_stack: string[]
  setup_completed: boolean
}

const FieldLabel = ({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
}) => (
  <Label
    htmlFor={htmlFor}
    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1'
  >
    {children}
    {required && <span className='text-destructive'>*</span>}
  </Label>
)

const EditVendorModal = ({ open, onClose, vendorToEdit }: Props) => {
  const [updateVendor, { isLoading }] = useUpdateVendorMutation()
  /* Vendor type is a one-time decision made the first time setup is
     completed - freelancer_name/company_name are mutually exclusive to it,
     so once ClickUp/a prior edit has set it, it's locked to a single
     read-only tab instead of a switchable toggle. */
  const isTypeLocked = !!vendorToEdit.vendor_type

  const initialValues: FormValues = {
    vendor_name: vendorToEdit.vendor_name ?? '',
    vendor_type: vendorToEdit.vendor_type ?? 'company',
    contact_name: vendorToEdit.contact_name ?? '',
    contact_email: vendorToEdit.contact_email ?? '',
    contact_phone: vendorToEdit.contact_phone ?? '',
    default_rate: vendorToEdit.default_rate ?? '',
    freelancer_name: vendorToEdit.freelancer_name ?? '',
    company_name: vendorToEdit.company_name ?? '',
    address: vendorToEdit.address ?? '',
    tech_stack: vendorToEdit.tech_stack ?? [],
    // Coerce in case the API returns the raw MySQL TINYINT (0/1) rather
    // than a real boolean - the backend now tolerates either, but keeping
    // Formik state a genuine boolean avoids relying on that.
    setup_completed: !!vendorToEdit.setup_completed,
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-h-[92vh] overflow-y-auto p-0 gap-0 max-w-lg'>
        <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
              <Edit className='w-4 h-4 text-orange-500' />
            </div>
            <div>
              <DialogTitle className='text-base font-semibold leading-tight'>
                Edit Vendor
              </DialogTitle>
              <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                Update details for {vendorToEdit.vendor_name}.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Formik<FormValues>
          key={vendorToEdit.vendor_id}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={EditVendorSchema}
          onSubmit={async (values, { resetForm }) => {
            const payload = {
              vendor_name: values.vendor_name,
              vendor_type: values.vendor_type,
              contact_name: values.contact_name,
              contact_email: values.contact_email,
              contact_phone: values.contact_phone,
              ...(values.default_rate !== '' && Number(values.default_rate) > 0
                ? { default_rate: Number(values.default_rate) }
                : {}),
              ...(values.vendor_type === 'freelance' && values.freelancer_name
                ? { freelancer_name: values.freelancer_name }
                : {}),
              ...(values.vendor_type === 'company' && values.company_name
                ? { company_name: values.company_name }
                : {}),
              ...(values.address ? { address: values.address } : {}),
              ...(values.tech_stack.length > 0
                ? { tech_stack: values.tech_stack }
                : {}),
            }

            try {
              await updateVendor({
                id: vendorToEdit.vendor_id,
                ...payload,
                setup_completed: !!values.setup_completed,
              }).unwrap()
              toast.success('Vendor updated successfully')
              resetForm()
              onClose()
            } catch (err: unknown) {
              const error = err as { data?: { message?: string } }
              toast.error(error?.data?.message ?? 'Failed to update vendor')
            }
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <div className='px-6 py-5 space-y-5'>
              {/* Vendor Type - locked to a single read-only tab once set */}
              <div className='space-y-2'>
                <FieldLabel required>Vendor Type</FieldLabel>
                {isTypeLocked ? (
                  <div className='flex items-center gap-2 py-2.5 px-3 rounded-lg border border-border bg-muted/30 text-xs font-medium text-foreground'>
                    {values.vendor_type === 'company' ? (
                      <>
                        <Building2 className='w-3.5 h-3.5 text-orange-500' />
                        Company
                      </>
                    ) : (
                      <>
                        <User className='w-3.5 h-3.5 text-orange-500' />
                        Freelancer
                      </>
                    )}
                  </div>
                ) : (
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      onClick={() => setFieldValue('vendor_type', 'company')}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-medium border transition-all',
                        values.vendor_type === 'company'
                          ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                          : 'bg-card border-border text-muted-foreground hover:border-orange-500/50 hover:text-foreground'
                      )}
                    >
                      <Building2 className='w-3.5 h-3.5' />
                      Company
                    </button>
                    <button
                      type='button'
                      onClick={() => setFieldValue('vendor_type', 'freelance')}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs font-medium border transition-all',
                        values.vendor_type === 'freelance'
                          ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                          : 'bg-card border-border text-muted-foreground hover:border-orange-500/50 hover:text-foreground'
                      )}
                    >
                      <User className='w-3.5 h-3.5' />
                      Freelancer
                    </button>
                  </div>
                )}
              </div>

              {/* Vendor Name */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='cv-vendor_name' required>
                  Vendor Name
                </FieldLabel>
                <Input
                  id='cv-vendor_name'
                  name='vendor_name'
                  placeholder={
                    values.vendor_type === 'freelance'
                      ? 'e.g. John Doe Development'
                      : 'e.g. Acme Software Pvt Ltd'
                  }
                  value={values.vendor_name}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.vendor_name && touched.vendor_name
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.vendor_name && touched.vendor_name && (
                  <p className='text-xs text-destructive'>
                    {errors.vendor_name}
                  </p>
                )}
              </div>

              {/* Freelancer / Company specific name */}
              {values.vendor_type === 'freelance' ? (
                <div className='space-y-1.5'>
                  <FieldLabel htmlFor='cv-freelancer_name'>
                    Freelancer Full Name
                  </FieldLabel>
                  <Input
                    id='cv-freelancer_name'
                    name='freelancer_name'
                    placeholder='e.g. John Doe'
                    value={values.freelancer_name}
                    onChange={handleChange}
                    className='h-9 text-sm'
                  />
                </div>
              ) : (
                <div className='space-y-1.5'>
                  <FieldLabel htmlFor='cv-company_name'>
                    Company / Legal Name
                  </FieldLabel>
                  <Input
                    id='cv-company_name'
                    name='company_name'
                    placeholder='e.g. Acme Software Pvt Ltd'
                    value={values.company_name}
                    onChange={handleChange}
                    className='h-9 text-sm'
                  />
                </div>
              )}

              {/* Address */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='cv-address'>Address</FieldLabel>
                <Input
                  id='cv-address'
                  name='address'
                  placeholder='Street, city, country'
                  value={values.address}
                  onChange={handleChange}
                  className='h-9 text-sm'
                />
              </div>

              {/* Tech stack */}
              <div className='space-y-1.5'>
                <FieldLabel>Tech Stack / Skills</FieldLabel>
                <SkillsInput
                  value={values.tech_stack}
                  onChange={next => setFieldValue('tech_stack', next)}
                  placeholder='Add a skill, then press Enter'
                />
              </div>

              {/* Contact fields */}
              <div className='space-y-3 pt-1 border-t border-border'>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground pt-1'>
                  Contact Details
                </p>

                <div className='space-y-1.5'>
                  <FieldLabel htmlFor='cv-contact_name' required>
                    Contact Name
                  </FieldLabel>
                  <Input
                    id='cv-contact_name'
                    name='contact_name'
                    placeholder='Primary contact person'
                    value={values.contact_name}
                    onChange={handleChange}
                    className={cn(
                      'h-9 text-sm',
                      errors.contact_name && touched.contact_name
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : ''
                    )}
                  />
                  {errors.contact_name && touched.contact_name && (
                    <p className='text-xs text-destructive'>
                      {errors.contact_name}
                    </p>
                  )}
                </div>

                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <FieldLabel htmlFor='cv-contact_email' required>
                      Email
                    </FieldLabel>
                    <Input
                      id='cv-contact_email'
                      name='contact_email'
                      type='email'
                      placeholder='contact@vendor.com'
                      value={values.contact_email}
                      onChange={handleChange}
                      className={cn(
                        'h-9 text-sm',
                        errors.contact_email && touched.contact_email
                          ? 'border-destructive focus-visible:ring-destructive/30'
                          : ''
                      )}
                    />
                    {errors.contact_email && touched.contact_email && (
                      <p className='text-xs text-destructive'>
                        {errors.contact_email}
                      </p>
                    )}
                  </div>

                  <div className='space-y-1.5'>
                    <FieldLabel htmlFor='cv-contact_phone' required>
                      Phone
                    </FieldLabel>
                    <Input
                      id='cv-contact_phone'
                      name='contact_phone'
                      type='tel'
                      placeholder='+91 XXXXX XXXXX'
                      value={values.contact_phone}
                      onChange={handleChange}
                      className={cn(
                        'h-9 text-sm',
                        errors.contact_phone && touched.contact_phone
                          ? 'border-destructive focus-visible:ring-destructive/30'
                          : ''
                      )}
                    />
                    {errors.contact_phone && touched.contact_phone && (
                      <p className='text-xs text-destructive'>
                        {errors.contact_phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Default Rate */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='cv-default_rate'>
                  Default Rate ($/hr)
                </FieldLabel>
                <Input
                  id='cv-default_rate'
                  name='default_rate'
                  type='number'
                  min={0}
                  step='0.01'
                  placeholder='0.00'
                  value={values.default_rate}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.default_rate && touched.default_rate
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.default_rate && touched.default_rate && (
                  <p className='text-xs text-destructive'>
                    {String(errors.default_rate)}
                  </p>
                )}
              </div>

              {/* Setup completed */}
              <div className='flex items-center justify-between py-3 px-3 rounded-lg border border-border bg-muted/20'>
                <div>
                  <FieldLabel>Setup Completed</FieldLabel>
                  <p className='text-[11px] text-muted-foreground mt-0.5'>
                    Mark this vendor as ready to use on projects
                  </p>
                </div>
                <Switch
                  checked={values.setup_completed}
                  onCheckedChange={checked =>
                    setFieldValue('setup_completed', checked)
                  }
                  className='data-[state=checked]:bg-orange-500'
                />
              </div>

              {/* Footer */}
              <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onClose}
                  className='h-8 text-xs border-border hover:bg-accent/30'
                >
                  Cancel
                </Button>
                <Button
                  size='sm'
                  onClick={() => handleSubmit()}
                  disabled={isLoading}
                  className='h-8 text-xs min-w-[100px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-3.5 h-3.5 mr-1.5 animate-spin' />
                      Saving…
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditVendorModal
